import { ChangeDetectorRef, Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { formDetectChanges } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  DialogRef,
  NotificationStyle,
  LabelComponent,
  FormFieldComponent,
  HeadingComponent,
  AvatarComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
  ButtonLoadingDirective,
  DialogFooterComponent,
  IconButtonComponent,
  InputComponent,
  ButtonComponent,
  FormErrorDirective,
} from '@core/user-interface-legacy';

import type { AuthenticationFormGroup, User } from '../authentication-dialog.service';
import {
  AuthenticationDialogService,
  AuthenticationVerificationType,
} from '../authentication-dialog.service';
import { CodeInputComponent } from '../code-input/code-input.component';
import { injectAuthTranslations } from '../translations';

type AuthenticationFormKeys = keyof AuthenticationFormGroup['controls'];

@Component({
  selector: 'rt-authentication-dialog',
  templateUrl: './authentication-dialog.component.html',
  styleUrls: ['./authentication-dialog.component.less'],
  host: {
    '(keydown.enter)': 'submit()',
  },
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    AvatarComponent,
    HeadingComponent,
    FormFieldComponent,
    LabelComponent,
    CodeInputComponent,
    FormErrorDirective,
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    InfoboxComponent,
    IconButtonComponent,
    DialogFooterComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class AuthenticationDialogComponent
  implements
    DialogComponentI<{ colorizedAvatar?: boolean; noCurrentCompany?: boolean }, string | undefined>
{
  public readonly VerificationType: typeof AuthenticationVerificationType =
    AuthenticationVerificationType;

  public translationKey = injectAuthTranslations();
  public errorMessages: Record<keyof AuthenticationFormGroup['controls'], Record<string, string>>;
  public canChangeVerificationType: boolean;
  public user: User;
  public fullName: string;
  public passwordUsageDisabled: boolean = true;

  public selectedVerification: AuthenticationVerificationType;
  public authenticationForm: AuthenticationFormGroup;
  public isLoading: boolean = false;
  public inputType: 'text' | 'password' = 'password';
  public visibilityIcon: 'visibility-off' | 'visibility' = 'visibility';
  public colorizedAvatar: boolean = false;

  private ngOnDestroy$ = new Subject<void>();
  private emailNotSend: boolean = true;
  private noCurrentCompany: boolean = false;

  constructor(
    protected cdRef: ChangeDetectorRef,
    private dialog: DialogRef,
    private authenticationDialogService: AuthenticationDialogService,
  ) {
    this.authenticationForm = this.authenticationDialogService.createAuthForm();
  }

  public ngOnInit(): void {
    this.errorMessages = this.authenticationDialogService.errorMessages;
    this.canChangeVerificationType = this.authenticationDialogService.canChangeVerificationType;
    this.user = this.authenticationDialogService.user;
    this.fullName = `${this.user.firstName} ${this.user.lastName}`;
    this.passwordUsageDisabled = this.authenticationDialogService.passwordUsageDisabled;

    const initialVerificationType = this.authenticationDialogService.initialVerificationType;

    formDetectChanges(this.authenticationForm, this.cdRef, this.ngOnDestroy$);
    this.onVerificationTypeChange(initialVerificationType);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public submit(): void {
    if (this.authenticationForm.invalid) return;

    this.toggleLoading();
    this.authenticationDialogService
      .submit(this.authenticationForm, this.noCurrentCompany)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(
        token => {
          this.toggleLoading();
          this.dialog.close(token);
        },
        () => {
          Object.keys(this.authenticationForm.controls).forEach(
            (controlName: AuthenticationFormKeys) => {
              if (
                this.authenticationForm.controls[controlName].enabled &&
                this.errorMessages[controlName]
              ) {
                this.authenticationForm.controls[controlName].setErrors(
                  this.errorMessages[controlName],
                );
              }
            },
          );
          this.toggleLoading();
        },
      );
  }

  public toggleVisibility(): void {
    this.inputType = this.inputType === 'text' ? 'password' : 'text';
    this.visibilityIcon = this.inputType === 'text' ? 'visibility-off' : 'visibility';
    this.cdRef.detectChanges();
  }

  public onVerificationTypeChange(type: AuthenticationVerificationType): void {
    const { password, emailCode } = this.authenticationForm.controls;
    this.selectedVerification = type;

    // eslint-disable-next-line default-case
    switch (type) {
      case AuthenticationVerificationType.EMAIL_CODE:
        if (this.emailNotSend) {
          this.requestEmailCode();
        }
        password.disable();
        emailCode.enable();
        break;

      case AuthenticationVerificationType.PASSWORD:
        this.passwordUsageDisabled ? password.disable() : password.enable();
        emailCode.disable();
        break;
    }
    this.cdRef.detectChanges();
  }

  public requestEmailCode(): void {
    this.emailNotSend = false;
    const notificationInfo = this.authenticationDialogService.createEmailCodeProgressNotification();

    this.authenticationDialogService
      .requestEmailCode()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(
        () =>
          void Object.assign(
            notificationInfo,
            this.authenticationDialogService.notificationStyle[NotificationStyle.Success],
          ),
        () =>
          void Object.assign(
            notificationInfo,
            this.authenticationDialogService.notificationStyle[NotificationStyle.Error],
          ),
      );
  }

  public closeDialog(): void {
    this.dialog.close();
  }

  public setDialogData(data: { colorizedAvatar?: boolean; noCurrentCompany?: boolean }): void {
    if (!data) return;
    const { colorizedAvatar, noCurrentCompany } = data;
    if (colorizedAvatar !== undefined) {
      this.colorizedAvatar = colorizedAvatar;
    }
    if (noCurrentCompany !== undefined) {
      this.noCurrentCompany = noCurrentCompany;
    }
  }

  private toggleLoading(): void {
    this.isLoading = !this.isLoading;
    this.cdRef.detectChanges();
  }
}
